import React, { FC } from 'react';
import {
  ClientOptionsType,
  CoverageType,
} from 'QuoteApp/services/endpoints/GetConfig';
import { useInitializeCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useInitializeCoverages';
import { useSetMinimumCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useSetMinimumCoverages';
import styles from './styles.module.css';
import { Column, FormInput, Row } from 'Shared/components';
import { getIn, useFormikContext } from 'formik';
import { CoveragesValuesType } from '.';
import { Asset, Tooltip, HelpIcon, Alert } from '@clearcover/atlas';
import classNames from 'classnames';
type CoverageInputPropsType = {
  coverageFormKey: string;
  coverageConfig?: CoverageType<ClientOptionsType>;
  stackedCoverages?: CoverageType<ClientOptionsType>[];
};

const getCoverageAlertText = (
  coverageConfig: CoverageInputPropsType['coverageConfig']
) => {
  if (coverageConfig?.coverageType === 'limited_tort') {
    return 'Selecting Limited Tort coverage requires a customer signature prior to the effective date. If no signature is collected, Full Tort coverage will be added back to the policy and the customer will be charged the Full Tort premium.';
  }

  return 'A signature is required to waive this coverage. If the customer fails to sign the waiver and policy documents then the customer may be charged for additional coverages or the policy may be canceled.';
};

export const CoverageInput: FC<CoverageInputPropsType> = ({
  coverageConfig,
  coverageFormKey,
  stackedCoverages,
}) => {
  const {
    handleBlur,
    handleSubmit,
    values,
    setFieldValue,
    isSubmitting,
    status,
  } = useFormikContext<CoveragesValuesType>();
  const { pending: initializeCoveragesLoading } = useInitializeCoverages();
  const { pending: setMinimumCoveragesLoading } = useSetMinimumCoverages();
  const coveragesUpdating =
    initializeCoveragesLoading || setMinimumCoveragesLoading;

  const healingMessage = getIn(status?.healing, coverageFormKey);
  const coverageValue = getIn(values, coverageFormKey);

  const isRequiredToWaive =
    coverageValue === 'waived' && coverageConfig?.signatureRequiredToWaive;
  const isRequiredToInclude =
    coverageValue === 'confirmed' && coverageConfig?.signatureRequiredToInclude;

  const errorMessage = initializeCoveragesLoading
    ? null
    : getIn(status?.errors, coverageFormKey);

  const coverageValueKey = coverageFormKey.substring(
    0,
    coverageFormKey.lastIndexOf('.')
  );

  const stackedCoveragesErrorMessages = stackedCoverages?.map(c =>
    getIn(status?.errors, `${coverageValueKey}.${c.coverageType}`)
  );

  const allErrorMessages = [
    ...(Array.isArray(errorMessage)
      ? errorMessage
      : errorMessage
      ? [errorMessage]
      : []),
    ...(stackedCoveragesErrorMessages || []),
  ].filter(m => m !== undefined);

  const stackedCoveragesHealingMessages = stackedCoverages?.map(c =>
    getIn(status?.healing, `${coverageValueKey}.${c.coverageType}`)
  );

  const allHealingMessages = [
    healingMessage,
    ...(stackedCoveragesHealingMessages || []),
  ].filter(m => m !== undefined);

  return (
    <div className={styles.coverageItemContainer} data-testid={coverageFormKey}>
      <Row className={styles.coverageItemRow}>
        <Column className={styles.labelColumn}>
          <div className={styles.coverageLabel}>
            <h4>{coverageConfig?.name}</h4>
            <Tooltip
              tooltip={
                <p className={styles.tooltip}>
                  {coverageConfig?.shortDescription?.replace(/\\n/g, '\n\n')}
                </p>
              }
              position="right"
            >
              <Asset
                icon={HelpIcon}
                description={`${coverageConfig?.coverageLabel} information`}
                height={17}
                width={17}
                className={styles.helpIcon}
              />
            </Tooltip>
          </div>
        </Column>
        <Column className={styles.optionsColumn}>
          <FormInput
            name={coverageFormKey}
            onBlur={handleBlur}
            label={coverageConfig?.coverageLabel || ''}
            value={coverageValue}
            errorMessage={errorMessage}
            renderErrorMessage={false}
            loading={isSubmitting}
            disabled={isSubmitting || coveragesUpdating}
            variant="select"
            data-testid={`input-${coverageFormKey}`}
            onChange={(value: string) => {
              setFieldValue(coverageFormKey, value);
              handleSubmit();
            }}
            options={coverageConfig?.coverageLevels || {}}
            className={styles.inputOption}
          />
          {stackedCoverages?.map(c => (
            <FormInput
              key={c.coverageType}
              name={`${coverageValueKey}.${c.coverageType}`}
              onBlur={handleBlur}
              label={c.coverageLabel}
              value={getIn(values, `${coverageValueKey}.${c.coverageType}`)}
              errorMessage={getIn(
                status?.errors,
                `${coverageValueKey}.${c.coverageType}`
              )}
              renderErrorMessage={false}
              loading={isSubmitting}
              disabled={isSubmitting || coveragesUpdating}
              variant="select"
              data-testid={`input-${coverageValueKey}.${c.coverageType}`}
              options={c.coverageLevels || {}}
              className={styles.inputOption}
              onChange={(value: string) => {
                setFieldValue(`${coverageValueKey}.${c.coverageType}`, value);
                handleSubmit();
              }}
            />
          ))}
        </Column>
      </Row>
      {!!allErrorMessages?.length && (
        <Row>
          <Column>
            <Alert
              data-testid={`error-${coverageFormKey}`}
              className={classNames(
                styles.warningAlert,
                styles.errorMessageAlert
              )}
              type="error"
              message={
                <div className={styles.errorsWrapper}>
                  {allErrorMessages.map((message, index) => (
                    <span key={index}>{message}</span>
                  ))}
                </div>
              }
            />
          </Column>
        </Row>
      )}
      {(isRequiredToWaive || isRequiredToInclude) && (
        <Row>
          <Column>
            <Alert
              data-testid={`alert-${coverageFormKey}`}
              className={styles.warningAlert}
              type="warning-default"
              message={getCoverageAlertText(coverageConfig)}
            />
          </Column>
        </Row>
      )}
      {!!allHealingMessages?.length && (
        <Row>
          <Column>
            <div data-testid="healed-message" className={styles.healingMessage}>
              {allHealingMessages.map((message, index) => (
                <span key={index}>{message}</span>
              ))}
            </div>
          </Column>
        </Row>
      )}
    </div>
  );
};
